import { useCallback, useEffect, useState } from 'react';
import { api } from './api.ts';

/**
 * A read with the three states a surface must actually distinguish: loading,
 * failed, and loaded. There is deliberately no fourth "loaded but empty" state
 * here — whether empty means "nothing yet" or "not built yet" is a domain
 * question the page answers, not something a fetch hook can know.
 */
export type Resource<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'failed'; readonly error: unknown }
  | { readonly status: 'loaded'; readonly data: T };

export function useResource<T>(path: string | null): {
  resource: Resource<T>;
  reload: () => void;
} {
  const [nonce, setNonce] = useState(0);
  /**
   * The result is stored with the request it belongs to.
   *
   * That is what lets a path change read as `loading` without a synchronous
   * `setState` inside the effect — the stale result simply stops matching the
   * current request, rather than being cleared and re-rendered.
   */
  const [settled, setSettled] = useState<{ key: string; resource: Resource<T> } | null>(null);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  const key = `${path ?? ''}#${String(nonce)}`;

  useEffect(() => {
    if (path === null) return;
    let cancelled = false;

    api
      .request<T>(path)
      .then((data: T) => {
        if (!cancelled) setSettled({ key, resource: { status: 'loaded', data } });
      })
      .catch((error: unknown) => {
        if (!cancelled) setSettled({ key, resource: { status: 'failed', error } });
      });

    return () => {
      cancelled = true;
    };
  }, [path, key]);

  const resource: Resource<T> =
    settled !== null && settled.key === key ? settled.resource : { status: 'loading' };

  return { resource, reload };
}
